import { useState, useEffect, useRef } from 'react';

export type UseIntersectionObserverOptions = IntersectionObserverInit;

/**
 * Хук, который отслеживает пересечение элемента с родительским контейнером (по умолчанию window),
 * возвращает массив из рефа (на элемент) и объекта типа IntersectionObserverEntry.
 *
 * @param options - Опции условий наблюдения за пересечением.
 * @returns Кортеж [ref, entry].
 */
export function useIntersectionObserver(
  options: UseIntersectionObserverOptions = {},
): [React.MutableRefObject<null>, IntersectionObserverEntry | null] {
  const {
    threshold = 1,
    root = null,
    rootMargin = '0%',
  }: IntersectionObserverInit = options;
  const ref = useRef(null);
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);

  useEffect(() => {
    const node = ref?.current;

    if (!node || typeof IntersectionObserver !== 'function') {
      return;
    }

    const observer = new IntersectionObserver(
      ([entryObserver]) => {
        setEntry(entryObserver);
      },
      { threshold, root, rootMargin },
    );

    observer.observe(node);

    return () => {
      setEntry(null);
      observer.disconnect();
    };
  }, [threshold, root, rootMargin]);

  return [ref, entry];
}
